import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { accountApi, apiClient, getErrorMessage, ProfileDetails } from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import Modal from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Notice } from '@/components/ui/Notice';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { checkbox } from '@/components/ui/fieldStyles';
import { Download, Trash2, X, ShieldAlert } from 'lucide-react';

const IP_OR_CIDR_REGEX = /^(\d{1,3}\.){3}\d{1,3}(\/(\d|[12]\d|3[0-2]))?$/;

type AlertMessage = { type: 'success' | 'error'; message: string } | null;

interface AccountActivityEntry {
  id: string;
  event_type: string;
  ip_address: string;
  severity: string;
  timestamp: number;
}

const ACTIVITY_LABELS: Record<string, string> = {
  login_attempt: 'Sign-in',
  logout: 'Sign-out',
  registration: 'Account created',
  token_created: 'API key created',
  password_changed: 'Password changed',
  ip_whitelist_changed: 'IP whitelist changed',
  account_deletion_requested: 'Account deletion requested',
};

const NOTIFICATION_LABELS: Record<string, string> = {
  coupon_redeemed: 'Coupon redeemed',
  payment_succeeded: 'Payment succeeded',
  payment_failed: 'Payment failed',
  token_created: 'API key created',
  token_revoked: 'API key revoked',
  token_rotated: 'API key rotated',
  role_changed: 'Team role changed',
  low_balance: 'Low credit balance (also by email)',
};

const SettingsPage: React.FC = () => {
  const { refreshUser, logout } = useAuth();

  const [profile, setProfile] = useState<ProfileDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState<AlertMessage>(null);

  const [profileForm, setProfileForm] = useState({ full_name: '', company: '', phone: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [savingPassword, setSavingPassword] = useState(false);

  const [notificationPrefs, setNotificationPrefs] = useState<Record<string, boolean>>({});
  const [savingNotifications, setSavingNotifications] = useState(false);

  const [savingConsent, setSavingConsent] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [ipWhitelist, setIpWhitelist] = useState<string[]>([]);
  const [newIpEntry, setNewIpEntry] = useState('');
  const [ipEntryError, setIpEntryError] = useState<string | null>(null);
  const [savingWhitelist, setSavingWhitelist] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  const [activity, setActivity] = useState<AccountActivityEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);

  const loadProfile = async () => {
    try {
      const { data } = await accountApi.getProfile();
      setProfile(data);
      setProfileForm({
        full_name: data.full_name ?? '',
        company: data.company ?? '',
        phone: data.phone ?? '',
      });
      const prefs: Record<string, boolean> = {};
      for (const type of Object.keys(NOTIFICATION_LABELS)) {
        prefs[type] = data.notification_prefs?.[type] ?? true;
      }
      setNotificationPrefs(prefs);
      setIpWhitelist(data.ip_whitelist ?? []);
    } catch (err) {
      setAlertMessage({ type: 'error', message: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
    apiClient
      .get('/account/activity', { params: { limit: 10 } })
      .then(({ data }) => setActivity(data.logs ?? []))
      .catch(() => setActivity([]))
      .finally(() => setActivityLoading(false));
  }, []);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await accountApi.updateProfile(profileForm);
      setProfile(data);
      await refreshUser();
      setAlertMessage({ type: 'success', message: 'Profile updated.' });
    } catch (err) {
      setAlertMessage({ type: 'error', message: getErrorMessage(err) });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.next !== passwordForm.confirm) {
      setAlertMessage({ type: 'error', message: 'New passwords do not match.' });
      return;
    }
    setSavingPassword(true);
    try {
      await accountApi.changePassword(passwordForm.current, passwordForm.next);
      setPasswordForm({ current: '', next: '', confirm: '' });
      setAlertMessage({ type: 'success', message: 'Password changed.' });
    } catch (err) {
      setAlertMessage({ type: 'error', message: getErrorMessage(err) });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      const { data } = await accountApi.updateProfile({ notification_prefs: notificationPrefs });
      setProfile(data);
      setAlertMessage({ type: 'success', message: 'Notification preferences saved.' });
    } catch (err) {
      setAlertMessage({ type: 'error', message: getErrorMessage(err) });
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleAddIpEntry = () => {
    const entry = newIpEntry.trim();
    setIpEntryError(null);
    if (!entry) return;
    if (!IP_OR_CIDR_REGEX.test(entry)) {
      setIpEntryError('Enter a valid IPv4 address (e.g. 203.0.113.4) or CIDR range (e.g. 203.0.113.0/24).');
      return;
    }
    if (ipWhitelist.includes(entry)) {
      setIpEntryError('That entry is already in the list.');
      return;
    }
    setIpWhitelist((list) => [...list, entry]);
    setNewIpEntry('');
  };

  const handleRemoveIpEntry = (entry: string) => {
    setIpWhitelist((list) => list.filter((ip) => ip !== entry));
  };

  const handleSaveWhitelist = async () => {
    setSavingWhitelist(true);
    try {
      const { data } = await accountApi.updateProfile({ ip_whitelist: ipWhitelist });
      setProfile(data);
      setIpWhitelist(data.ip_whitelist ?? []);
      setAlertMessage({ type: 'success', message: 'IP whitelist saved.' });
    } catch (err) {
      setAlertMessage({ type: 'error', message: getErrorMessage(err) });
    } finally {
      setSavingWhitelist(false);
    }
  };

  const handleConsentChange = async (field: 'marketing_consent' | 'data_processing_consent', value: boolean) => {
    setSavingConsent(true);
    try {
      const { data } = await accountApi.updateProfile({ [field]: value });
      setProfile(data);
      setAlertMessage({ type: 'success', message: 'Consent preferences updated.' });
    } catch (err) {
      setAlertMessage({ type: 'error', message: getErrorMessage(err) });
    } finally {
      setSavingConsent(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const { data } = await accountApi.exportData();
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'latlens-data-export.json';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setAlertMessage({ type: 'error', message: getErrorMessage(err) });
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await accountApi.deleteAccount(deletePassword);
      await logout();
    } catch (err) {
      setAlertMessage({ type: 'error', message: getErrorMessage(err) });
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl space-y-6" aria-hidden="true">
        <div><div className="skeleton h-8 w-40" /><div className="skeleton mt-2 h-4 w-64" /></div>
        {[0, 1].map((i) => (
          <div key={i} className="rounded-2xl border border-border bg-surface p-5">
            <div className="skeleton h-4 w-24" />
            <SkeletonLines count={3} className="mt-5" lineClassName="h-11" />
          </div>
        ))}
      </div>
    );
  }

  const activityIcon = <ShieldAlert />;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader title="Settings" description="Your profile, security and data." />

      {/* Profile */}
      <Panel title="Profile">
        <form onSubmit={handleSaveProfile} className="space-y-4">
          <Input label="Email address" type="email" value={profile?.email ?? ''} disabled />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Full name"
              value={profileForm.full_name}
              onChange={(e) => setProfileForm((f) => ({ ...f, full_name: e.target.value }))}
            />
            <Input
              label="Company"
              value={profileForm.company}
              onChange={(e) => setProfileForm((f) => ({ ...f, company: e.target.value }))}
            />
          </div>
          <Input
            label="Phone"
            value={profileForm.phone}
            onChange={(e) => setProfileForm((f) => ({ ...f, phone: e.target.value }))}
          />
          <div className="flex justify-end border-t border-border pt-4">
            <Button type="submit" loading={savingProfile}>Save profile</Button>
          </div>
        </form>
      </Panel>

      {/* Password */}
      <Panel title="Change password">
        <form onSubmit={handleChangePassword} className="space-y-4">
          <Input
            label="Current password"
            type="password"
            autoComplete="current-password"
            value={passwordForm.current}
            onChange={(e) => setPasswordForm((f) => ({ ...f, current: e.target.value }))}
            required
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="New password"
              type="password"
              autoComplete="new-password"
              value={passwordForm.next}
              onChange={(e) => setPasswordForm((f) => ({ ...f, next: e.target.value }))}
              required
            />
            <Input
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              value={passwordForm.confirm}
              onChange={(e) => setPasswordForm((f) => ({ ...f, confirm: e.target.value }))}
              required
            />
          </div>
          <div className="flex justify-end border-t border-border pt-4">
            <Button type="submit" loading={savingPassword}>Change password</Button>
          </div>
        </form>
      </Panel>

      {/* Recent account activity: read-only audit trail of your own sign-ins and security events */}
      <Panel title="Recent activity" bodyClassName="px-0 pb-2">
        {activityLoading ? (
          <SkeletonLines count={3} className="px-5 pb-3" />
        ) : activity.length === 0 ? (
          <EmptyState icon={activityIcon} title="No recent activity." description="Sign-ins and security changes show up here." className="py-8" />
        ) : (
          <ul className="divide-y divide-border">
            {activity.map((entry) => (
              <li key={entry.id} className="flex flex-col gap-0.5 px-5 py-3 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <span className="text-foreground">{ACTIVITY_LABELS[entry.event_type] ?? entry.event_type}</span>
                <span className="font-mono text-xs tabular-nums text-muted-foreground">
                  {new Date(entry.timestamp * 1000).toLocaleString()} · {entry.ip_address}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {/* IP whitelist: restricts which addresses your API keys accept requests from */}
      <Panel title="API key IP whitelist">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Only accept API key requests from these addresses. Leave empty to allow any IP.
          </p>

          {ipWhitelist.length > 0 && (
            <Notice tone="warning" icon={<ShieldAlert className="text-warning" />}>
              This only affects API key requests (the Gateway), not signing in here, so you can't lock yourself out of
              the dashboard this way. Make sure every server or service that calls your API keys is covered before
              saving, or their requests will start failing.
            </Notice>
          )}

          <ul className="space-y-2">
            {ipWhitelist.map((entry) => (
              <li key={entry} className="flex items-center justify-between rounded-xl border border-border px-3.5 py-2 text-sm">
                <span className="font-mono text-foreground">{entry}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveIpEntry(entry)}
                  className="rounded-full p-1 text-muted-foreground hover:bg-surface-raised hover:text-error"
                  aria-label={`Remove ${entry}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
            {ipWhitelist.length === 0 && (
              <li className="rounded-xl border border-dashed border-border px-3.5 py-3 text-sm text-muted-foreground">No restrictions. Keys accept requests from any IP.</li>
            )}
          </ul>

          <div className="flex items-start gap-2">
            <div className="flex-1">
              <Input
                aria-label="IP address or range"
                className="font-mono"
                placeholder="203.0.113.4 or 203.0.113.0/24"
                value={newIpEntry}
                onChange={(e) => { setNewIpEntry(e.target.value); setIpEntryError(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddIpEntry(); } }}
              />
            </div>
            <Button type="button" variant="outline" className="h-11" onClick={handleAddIpEntry}>Add</Button>
          </div>
          {ipEntryError && <p className="text-sm text-error">{ipEntryError}</p>}

          <div className="flex justify-end border-t border-border pt-4">
            <Button onClick={handleSaveWhitelist} loading={savingWhitelist}>Save whitelist</Button>
          </div>
        </div>
      </Panel>

      {/* Notification preferences */}
      <Panel title="Notification preferences">
        <p className="text-sm text-muted-foreground">Choose which in-app notifications you want to receive.</p>
        <div className="mt-4 grid gap-1 sm:grid-cols-2">
          {Object.entries(NOTIFICATION_LABELS).map(([type, label]) => (
            <label key={type} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-sm text-foreground hover:bg-surface-raised">
              <input
                type="checkbox"
                className={checkbox}
                checked={notificationPrefs[type] ?? true}
                onChange={(e) => setNotificationPrefs((p) => ({ ...p, [type]: e.target.checked }))}
              />
              {label}
            </label>
          ))}
        </div>
        <div className="mt-4 flex justify-end border-t border-border pt-4">
          <Button onClick={handleSaveNotifications} loading={savingNotifications}>Save preferences</Button>
        </div>
      </Panel>

      {/* Privacy & Data: Nigeria Data Protection Act 2023 */}
      <Panel title="Privacy & data">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Your rights under the Nigeria Data Protection Act (NDPA) 2023. See our{' '}
            <Link to="/privacy" className="text-accent-text hover:underline">Privacy Policy</Link> for full details on
            how your data is processed and how to reach our Data Protection Officer.
          </p>

          <label className="flex items-start gap-3 text-sm text-foreground">
            <input
              type="checkbox"
              className={`mt-0.5 ${checkbox}`}
              checked={profile?.data_processing_consent ?? true}
              disabled={savingConsent}
              onChange={(e) => handleConsentChange('data_processing_consent', e.target.checked)}
            />
            <span>
              <span className="font-medium">Data processing consent.</span>{' '}
              <span className="text-muted-foreground">
                Allow processing of your account data to operate the service (usage analytics, billing, fraud
                prevention). Withdrawing this may limit or disable your access, since it's required to deliver the
                service.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 text-sm text-foreground">
            <input
              type="checkbox"
              className={`mt-0.5 ${checkbox}`}
              checked={profile?.marketing_consent ?? false}
              disabled={savingConsent}
              onChange={(e) => handleConsentChange('marketing_consent', e.target.checked)}
            />
            <span>
              <span className="font-medium">Marketing communications.</span>{' '}
              <span className="text-muted-foreground">
                Receive product updates and promotional offers by email. Optional, and has no effect on service access.
              </span>
            </span>
          </label>

          {profile?.consent_updated_at && (
            <p className="text-xs text-muted-foreground">
              Consent last updated {new Date(profile.consent_updated_at * 1000).toLocaleString()}.
            </p>
          )}

          <div className="flex flex-wrap gap-3 border-t border-border pt-4">
            <Button variant="outline" onClick={handleExportData} loading={exporting} leftIcon={<Download className="h-4 w-4" />}>
              Export my data
            </Button>
            <Button variant="danger" onClick={() => setDeleteModalOpen(true)} leftIcon={<Trash2 className="h-4 w-4" />}>
              Delete my account
            </Button>
          </div>
        </div>
      </Panel>

      <Modal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Delete your account" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            This deactivates your account immediately and revokes all access, in line with your right to erasure
            under the NDPA. Enter your password to confirm.
          </p>
          <Input
            label="Password"
            type="password"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
          />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleDeleteAccount} loading={deleting} disabled={!deletePassword}>
              Delete account
            </Button>
          </div>
        </div>
      </Modal>

      {alertMessage && (
        <Alert
          type={alertMessage.type}
          title={alertMessage.type === 'success' ? 'Success' : 'Error'}
          message={alertMessage.message}
          isOpen={!!alertMessage}
          onClose={() => setAlertMessage(null)}
        />
      )}
    </div>
  );
};

export default SettingsPage;
